import React, { forwardRef, useState, useMemo, useRef, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Calendar, X } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';

import InfoTooltip from 'components/InfoTooltip';

import './styles.scss';

const parseDate = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {

    if (date.includes('-')) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    if (date.includes('/')) {
      const [month, day, year] = date.split('/').map(Number);
      return new Date(year, month - 1, day);
    }
  }
  return date;
};

const formatDateToString = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="flex items-center gap-2 w-full">
    <Calendar size={16} className="text-gray-400 flex-shrink-0" />
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className={`text-left text-sm bg-transparent focus:outline-none flex-1 ${value ? 'text-gray-900' : 'text-gray-400'}`}
    >
      {value || placeholder}
    </button>
  </div>
));

const DatePicker = forwardRef(({
  label,
  required = false,
  tooltip,
  error,
  className = '',
  placeholder = 'Select date',
  minDate,
  maxDate,
  dateFormat = 'MM/dd/yyyy',
  openToDate,
  onChange,
  value,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const wrapperRef = useRef(null);
  const internalRef = useRef(null);
  const datePickerRef = ref || internalRef;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleWrapperClick = () => {
    if (datePickerRef?.current && !isFocused) {
      datePickerRef.current.setOpen(true);
    }
  };

  const wrapperClasses = useMemo(() => {
    const base = 'space-y-2 rounded-lg bg-slate-50 border transition-all duration-200 p-3 cursor-pointer';

    if (error) {
      return `${base} ring-1 ${isFocused ? 'ring-red-400 border-red-400' : 'ring-red-300 border-red-300'} hover:border-red-400 hover:ring-red-400`;
    }

    if (isFocused) {
      return `${base} border-blue-400 ring-1`;
    }

    return `${base} border-slate-200 hover:border-gray-400`;
  }, [error, isFocused]);

  const handleCalendarOpen = () => {
    setIsFocused(true);
    if (isMobile) {
      setShowMobileModal(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const handleCalendarClose = () => {
    setIsFocused(false);
    if (isMobile) {
      setShowMobileModal(false);
      document.body.style.overflow = '';
    }
  };

  const handleChange = (date) => {
    if (onChange) {
      onChange(date ? formatDateToString(date) : '');
    }
  };

  return (
    <>
      <div className={`flex flex-col gap-2 ${className}`}>
        <div ref={wrapperRef} className={wrapperClasses} onClick={handleWrapperClick}>
          {label && (
            <div className="flex gap-2">
              <label className={`block text-xs uppercase font-bold transition-all duration-200 ${error ? 'text-red-500' : 'text-slate-500'}`}>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {tooltip && <InfoTooltip tooltip={tooltip} />}
            </div>
          )}
          <ReactDatePicker
            ref={datePickerRef}
            dateFormat={dateFormat}
            placeholderText={placeholder}
            minDate={parseDate(minDate)}
            maxDate={parseDate(maxDate)}
            openToDate={parseDate(openToDate)}
            selected={parseDate(value)}
            customInput={<CustomInput placeholder={placeholder} />}
            onCalendarOpen={handleCalendarOpen}
            onCalendarClose={handleCalendarClose}
            calendarClassName="custom-datepicker"
            popperClassName="custom-datepicker-popper"
            popperPlacement="bottom-start"
            withPortal={isMobile}
            onChange={handleChange}
            {...props}
          />
        </div>
        {error && error !== 'The input field is required' && <p className="text-xs text-red-500 px-2">{error}</p>}
      </div>
      {isMobile && showMobileModal && (
        <div className="react-datepicker__portal-overlay" onClick={handleCalendarClose} />
      )}
    </>
  );
});

export default DatePicker;
